import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { SpotlyteService } from '../../../spotlyte/services/spotlyte.service';
import { ToastService } from '../../services/toast/toast.service';
import { ToastType } from '../../models';
import { SpotlyteActions } from './spotlyte.actions';

function toSafeFilename(name: string): string {
    return name.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 50);
}

@Injectable()
export class SpotlyteEffects {
    private actions$ = inject(Actions);
    private spotlyteService = inject(SpotlyteService);
    private toastService = inject(ToastService);
    private document = inject(DOCUMENT);

    loadSources$ = createEffect(() => this.actions$.pipe(
        ofType(SpotlyteActions.loadSources),
        switchMap(() => this.spotlyteService.getSources().pipe(
            map(sources => SpotlyteActions.loadSourcesSuccess({ sources })),
            catchError(error => of(SpotlyteActions.loadSourcesFailure({ error }))),
        )),
    ));

    deleteSources$ = createEffect(() => this.actions$.pipe(
        ofType(SpotlyteActions.deleteSources),
        switchMap(({ ids }) => this.spotlyteService.deleteSources(ids).pipe(
            map(deleted => SpotlyteActions.deleteSourcesSuccess({ deleted })),
            catchError(error => of(SpotlyteActions.deleteSourcesFailure({ error }))),
        )),
    ));

    initiateUpload$ = createEffect(() => this.actions$.pipe(
        ofType(SpotlyteActions.initiateUpload),
        switchMap(({ request }) => this.spotlyteService.initiateUpload(request).pipe(
            map(response => SpotlyteActions.initiateUploadSuccess({ response })),
            catchError(error => of(SpotlyteActions.initiateUploadFailure({ error }))),
        )),
    ));

    completeUpload$ = createEffect(() => this.actions$.pipe(
        ofType(SpotlyteActions.completeUpload),
        switchMap(({ request }) => this.spotlyteService.completeUpload(request).pipe(
            map(source => SpotlyteActions.completeUploadSuccess({ source })),
            catchError(error => of(SpotlyteActions.completeUploadFailure({ error }))),
        )),
    ));

    loadJobs$ = createEffect(() => this.actions$.pipe(
        ofType(SpotlyteActions.loadJobs),
        switchMap(({ status }) => this.spotlyteService.getJobs(status).pipe(
            map(jobs => SpotlyteActions.loadJobsSuccess({ jobs })),
            catchError(error => of(SpotlyteActions.loadJobsFailure({ error }))),
        )),
    ));

    loadJob$ = createEffect(() => this.actions$.pipe(
        ofType(SpotlyteActions.loadJob),
        switchMap(({ jobId }) => this.spotlyteService.getJob(jobId).pipe(
            map(job => SpotlyteActions.loadJobSuccess({ job })),
            catchError(error => of(SpotlyteActions.loadJobFailure({ error }))),
        )),
    ));

    createVmsSources$ = createEffect(() => this.actions$.pipe(
        ofType(SpotlyteActions.createVmsSources),
        switchMap(({ requests }) => this.spotlyteService.ingestVmsSources(requests).pipe(
            tap(() => this.toastService.showToast(
                'Footage is being imported. This may take a few minutes.',
                ToastType.SUCCESS,
            )),
            map(sources => SpotlyteActions.createVmsSourcesSuccess({ sources })),
            catchError(error => {
                this.toastService.showToast('Failed to queue VMS sources for ingest', ToastType.ERROR);
                return of(SpotlyteActions.createVmsSourcesFailure({ error }));
            }),
        )),
    ));

    createJob$ = createEffect(() => this.actions$.pipe(
        ofType(SpotlyteActions.createJob),
        switchMap(({ request }) => this.spotlyteService.createJob(request).pipe(
            map(job => SpotlyteActions.createJobSuccess({ job })),
            catchError(error => of(SpotlyteActions.createJobFailure({ error }))),
        )),
    ));

    cancelJobs$ = createEffect(() => this.actions$.pipe(
        ofType(SpotlyteActions.cancelJobs),
        switchMap(({ ids }) => this.spotlyteService.cancelJobs(ids).pipe(
            map(cancelled => SpotlyteActions.cancelJobsSuccess({ cancelled })),
            catchError(error => of(SpotlyteActions.cancelJobsFailure({ error }))),
        )),
    ));

    loadResults$ = createEffect(() => this.actions$.pipe(
        ofType(SpotlyteActions.loadResults),
        switchMap(({ jobId }) => this.spotlyteService.getResults(jobId).pipe(
            map(results => SpotlyteActions.loadResultsSuccess({ results })),
            catchError(error => of(SpotlyteActions.loadResultsFailure({ error }))),
        )),
    ));

    loadResultBlobs$ = createEffect(() => this.actions$.pipe(
        ofType(SpotlyteActions.loadResultsSuccess),
        switchMap(({ results }) => {
            const summary$ = results.summaryUrl
                ? this.spotlyteService.getSummaryBlob(results.summaryUrl).pipe(catchError(() => of(null)))
                : of(null);
            const charts$ = results.chartsUrl
                ? this.spotlyteService.getChartsBlob(results.chartsUrl).pipe(catchError(() => of(null)))
                : of(null);
            const zones$ = results.zonesUrl
                ? this.spotlyteService.getZonesBlob(results.zonesUrl).pipe(catchError(() => of(null)))
                : of(null);
            return forkJoin({ summary: summary$, charts: charts$, zones: zones$ }).pipe(
                map(({ summary, charts, zones }) => SpotlyteActions.loadResultBlobsSuccess({
                    summary,
                    charts,
                    zones: zones ?? [],
                })),
                catchError(error => of(SpotlyteActions.loadResultBlobsFailure({ error }))),
            );
        }),
    ));

    loadFindings$ = createEffect(() => this.actions$.pipe(
        ofType(SpotlyteActions.loadFindings),
        switchMap(({ filters }) => this.spotlyteService.getFindings(filters).pipe(
            map(page => SpotlyteActions.loadFindingsSuccess({ page })),
            catchError(error => of(SpotlyteActions.loadFindingsFailure({ error }))),
        )),
    ));

    loadFindingsBlob$ = createEffect(() => this.actions$.pipe(
        ofType(SpotlyteActions.loadFindingsSuccess),
        switchMap(({ page }) => {
            if (!page.findingsUrl) {
                return of(SpotlyteActions.loadFindingsBlobSuccess({ findings: [] }));
            }
            return this.spotlyteService.getFindingsBlob(page.findingsUrl).pipe(
                map(findings => SpotlyteActions.loadFindingsBlobSuccess({ findings })),
                catchError(error => of(SpotlyteActions.loadFindingsBlobFailure({ error }))),
            );
        }),
    ));

    exportCsv$ = createEffect(() => this.actions$.pipe(
        ofType(SpotlyteActions.exportCsv),
        switchMap(({ jobId, jobName, createdAt }) => this.spotlyteService.exportCsv(jobId).pipe(
            tap(blob => {
                const date = createdAt ? createdAt.slice(0, 10) : '';
                const name = toSafeFilename(jobName);
                const url = URL.createObjectURL(blob);
                const a = this.document.createElement('a');
                a.href = url;
                a.download = `spotlyte_findings_${name}_${date}.csv`;
                a.click();
                setTimeout(() => URL.revokeObjectURL(url), 0);
            }),
            map(() => SpotlyteActions.exportCsvSuccess()),
            catchError(error => {
                this.toastService.showToast('Failed to export CSV', ToastType.ERROR);
                return of(SpotlyteActions.exportCsvFailure({ error }));
            }),
        )),
    ));

    exportPdf$ = createEffect(() => this.actions$.pipe(
        ofType(SpotlyteActions.exportPdf),
        switchMap(({ jobId, jobName, createdAt }) => this.spotlyteService.exportPdf(jobId).pipe(
            tap(blob => {
                const date = createdAt ? createdAt.slice(0, 10) : '';
                const name = toSafeFilename(jobName);
                const url = URL.createObjectURL(blob);
                const a = this.document.createElement('a');
                a.href = url;
                a.download = `spotlyte_report_${name}_${date}.pdf`;
                a.click();
                setTimeout(() => URL.revokeObjectURL(url), 0);
            }),
            map(() => SpotlyteActions.exportPdfSuccess()),
            catchError(error => {
                this.toastService.showToast('Failed to export PDF', ToastType.ERROR);
                return of(SpotlyteActions.exportPdfFailure({ error }));
            }),
        )),
    ));

    loadQnaHistory$ = createEffect(() => this.actions$.pipe(
        ofType(SpotlyteActions.loadQnaHistory),
        switchMap(({ jobId }) => this.spotlyteService.getQnaHistory(jobId).pipe(
            map(turns => SpotlyteActions.loadQnaHistorySuccess({ turns })),
            catchError(error => of(SpotlyteActions.loadQnaHistoryFailure({ error }))),
        )),
    ));

    askQuestion$ = createEffect(() => this.actions$.pipe(
        ofType(SpotlyteActions.askQuestion),
        switchMap(({ jobId, question }) => this.spotlyteService.askQuestion(jobId, question).pipe(
            map(turn => SpotlyteActions.askQuestionSuccess({ turn })),
            catchError(error => of(SpotlyteActions.askQuestionFailure({ error }))),
        )),
    ));

    askQuestionFailure$ = createEffect(() => this.actions$.pipe(
        ofType(SpotlyteActions.askQuestionFailure),
        map(() => this.toastService.showToast('Failed to get an answer. Please try again.', ToastType.ERROR)),
    ), { dispatch: false });
}
